import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import styles from './Network.module.css';

const STATUS_COLOR = {
  waiting:   '#9E9E9E',
  running:   '#09ff00',
  complete:  '#007bff',
  completed: '#007bff',
  error:     '#EF5350',
  timeout:   '#FF7043',
  unknown:   '#9E9E9E',
};

const MAX_DEVICE_LABEL_WIDTH = 40;
const INACTIVE_NODE_ALPHA = 0.05;
const INACTIVE_LINK_ALPHA = 0.05;

function add(a,b){ return {x:a.x+b.x,y:a.y+b.y,z:a.z+b.z}; }
function mul(a,s){ return {x:a.x*s,y:a.y*s,z:a.z*s}; }
function dot(a,b){ return a.x*b.x + a.y*b.y + a.z*b.z; }
function cross(a,b){ return {x:a.y*b.z - a.z*b.y, y:a.z*b.x - a.x*b.z, z:a.x*b.y - a.y*b.x}; }
function len(a){ return Math.sqrt(dot(a,a)); }
function norm(a){ const L=len(a)||1; return mul(a,1/L); }
function orthoBasis(n){ const k = Math.abs(n.x) > 0.8 ? {x:0,y:1,z:0} : {x:1,y:0,z:1}; const u = norm(cross(n, k)); const v = cross(n, u); return {u, v}; }
function rotateAxis(v, k, t){ const c = Math.cos(t), s = Math.sin(t); const kv = dot(k, v); return add(add(mul(v, c), mul(cross(k, v), s)), mul(k, (1 - c) * kv)); }

function normalizeStatus(s) {
  if (!s) return 'unknown';
  const k = String(s).toLowerCase();
  if (k === 'complete') return 'complete';
  if (k === 'completed') return 'completed';
  if (k === 'running') return 'running';
  if (k === 'waiting') return 'waiting';
  if (k === 'timeout' || k === 'time_out') return 'timeout';
  if (k === 'error' || k === 'failed' || k === 'failure') return 'error';
  return k;
}

function xfnv1a(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => { h += h << 13; h ^= h >>> 7; h += h << 3;  h ^= h >>> 17; h += h << 5; return (h >>> 0) / 4294967296; };
}

function ellipsizeText(ctx, text, maxWidth) {
  if (!text) return '';
  if (text==='FL Server') return 'FL Server';
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const cand = text.slice(0, mid) + '…';
    if (ctx.measureText(cand).width <= maxWidth) lo = mid + 1; else hi = mid;
  }
  const trimmed = text.slice(0, Math.max(0, lo - 1));
  return trimmed + '…';
}

function getLatestTaskInfo(tasks = []) {
  if (!tasks.length) return { roundNum: null, task: null, status: 'unknown' };
  const withStart = tasks.filter(t => t?.start);
  const latest = withStart.length
    ? withStart.reduce((a, b) => (new Date(a.start) > new Date(b.start) ? a : b))
    : tasks[tasks.length - 1];
  return {
    roundNum: latest?.roundNum ?? null,
    task: latest?.task ?? null,
    status: normalizeStatus(latest?.status),
  };
}

export default function Network({
  data = [],
  height = 800,
  fov = 60,
  style,
  showDeviceOrbits = true,
  showRoundOrbits  = false,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const tooltipRef = useRef(null);
  const infoRef = useRef(null);

  const state = useRef({
    yaw: 0.6,
    pitch: -0.15,
    zoomScale: 1,
    camBaseDist: 700,
    dragging: false,
    lastX: 0,
    lastY: 0,
    hoverIndex: -1,
    nodes: [],
    links: [],
    neighbors: new Map(),
    sceneRadius: 400,
    introStart: 0,
    introDur: 1400,
    introPlaying: false,
    introDelays: Object.create(null),
    nodeAlpha: [],
    linkAlpha: [],
    lastTs: 0,
    animStart: performance.now(),
    deviceOrbitSpeed: 0.05,
    moonOrbitSpeed:   0.05,
  }).current;

  useResizeObserver(containerRef, () => draw());

  useEffect(() => {
    const canvas = canvasRef.current;
    const zoom = d3.zoom()
      .scaleExtent([0.5, 5])
      .filter((event) => event.type === 'wheel' || event.type === 'touchmove' || event.type === 'touchstart')
      .on('zoom', (event) => { state.zoomScale = event.transform.k; requestRender(); });
    d3.select(canvas).call(zoom);

    const onPointerDown = (e) => { state.dragging = true; state.lastX = e.clientX; state.lastY = e.clientY; canvas.classList.add(styles.grabbing); };
    const onPointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left; const my = e.clientY - rect.top;
      if (state.dragging) {
        const dx = e.clientX - state.lastX; const dy = e.clientY - state.lastY;
        state.lastX = e.clientX; state.lastY = e.clientY;
        state.yaw += dx * 0.005; state.pitch += dy * 0.005;
        state.pitch = Math.max(-Math.PI/2 + 0.01, Math.min(Math.PI/2 - 0.01, state.pitch));
        requestRender();
      } else {
        const hit = pickNode(mx, my);
        const newHover = hit?.idx ?? -1;
        if (newHover !== state.hoverIndex) { state.hoverIndex = newHover; requestRender(); }
        showTooltip(hit, mx, my);
        showInfoPanel(hit);
      }
    };
    const onPointerLeave = () => { state.hoverIndex = -1; requestRender(); hideTooltip(); hideInfoPanel(); };
    const onPointerUp = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left; const my = e.clientY - rect.top;
      if (!state.dragging) {
        const hit = pickNode(mx, my);
        if (hit) {
          const pos = getAnimatedXYZ(state.nodes[hit.idx], performance.now());
          focusOnNode(pos);
        }
      }
      state.dragging = false; canvas.classList.remove(styles.grabbing);
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerLeave);

    state.introStart = performance.now();
    state.introPlaying = true;
    state.animStart = performance.now();
    requestRender();

    return () => {
      d3.select(canvas).on('.zoom', null);
      canvas.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  function buildGraph() {
    const w = containerRef.current?.clientWidth || 960;
    const r = Math.min(w, height) * 0.45;
    state.sceneRadius = r;

    const nodes = [];
    const links = [];

    const PHI = (1 + Math.sqrt(5)) / 2;
    const TWO_PI = Math.PI * 2;
    function fibSphereDir(i, n) {
      const k = i + 0.5;
      const y = 1 - (2 * k) / n;
      const rxy = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = TWO_PI * (k / (PHI * PHI));
      return norm({ x: rxy*Math.cos(theta), y, z: rxy*Math.sin(theta) });
    }

    const server = data.find(d => d?.name === 'server');
    const serverStatus = normalizeStatus(server?.status);
    nodes.push({
      id: 'server',
      name: 'FL Server',
      type: 'server',
      status: serverStatus,
      color: STATUS_COLOR[serverStatus] || '#007bff',
      x: 0, y: 0, z: 0,
      label: 'FL Server'
    });

    const devices = data.filter(d => d?.name !== 'server');
    const nDev = Math.max(1, devices.length);
    const orbitR = Math.max(300, r * 0.72);

    devices.forEach((d, i) => {
      const st = normalizeStatus(d?.status);
      const color = STATUS_COLOR[st] || STATUS_COLOR.unknown;
      const latest = getLatestTaskInfo(d?.tasks || []);
      const dirSeed = fibSphereDir(i, nDev);
      const rnd = xfnv1a(d.name);
      const orbitAxis = (() => {
        const u=rnd(), v=rnd();
        const th=2*Math.PI*u, ph=Math.acos(2*v-1);
        return norm({ x: Math.sin(ph)*Math.cos(th), y: Math.sin(ph)*Math.sin(th), z: Math.cos(ph) });
      })();
      const proj = dot(dirSeed, orbitAxis);
      let orbitDir0 = norm({
        x: dirSeed.x - orbitAxis.x*proj,
        y: dirSeed.y - orbitAxis.y*proj,
        z: dirSeed.z - orbitAxis.z*proj
      });
      if (!isFinite(orbitDir0.x)) {
        const k = Math.abs(orbitAxis.x) > 0.8 ? {x:0,y:1,z:0} : {x:1,y:0,z:0};
        orbitDir0 = norm(cross(orbitAxis, k));
      }

      const devIdx = nodes.push({
        id: `device:${d.name}`,
        name: d.name,
        type: 'device',
        status: st,
        color,
        latestRound: latest.roundNum,
        latestTask: latest.task,
        x: 0, y: 0, z: 0,
        label: d.name,
        _orbitDir0: orbitDir0,
        _orbitAxis: orbitAxis,
        _orbitR: orbitR,
        _basePhase: rnd()*Math.PI*2,
      }) - 1;

      links.push({ id: `link:server:${d.name}`, fromIdx: 0, toIdx: devIdx, status: st });

      const rounds = Array.from(new Map((d.tasks || []).map(t => [t?.roundNum, t])).entries())
        .filter(([rn]) => rn != null)
        .map(([rn, t]) => ({ roundNum: rn, status: normalizeStatus(t?.status) }));

      const m = rounds.length;
      if (m > 0) {
        const childR = Math.min(60, r * 0.22);
        const rndAxis = (() => {
          const u=rnd(), v=rnd();
          const th=2*Math.PI*u, ph=Math.acos(2*v-1);
          return norm({ x: Math.sin(ph)*Math.cos(th), y: Math.sin(ph)*Math.sin(th), z: Math.cos(ph) });
        });

        for (let j = 0; j < m; j++) {
          const rd = rounds[j];
          const nAxis = rndAxis();
          const {u, v} = orthoBasis(nAxis);
          const phase = rnd()*Math.PI*2;

          const childIdx = nodes.push({
            id: `round:${d.name}:${rd.roundNum}`,
            name: `${d.name}#${rd.roundNum}`,
            type: 'round',
            parentDevice: devIdx,
            roundNum: rd.roundNum,
            status: rd.status,
            color: STATUS_COLOR[rd.status] || STATUS_COLOR.waiting,
            x: 0, y: 0, z: 0,
            label: `${rd.roundNum}R`,
            _orbitN: nAxis,
            _u: u, _v: v,
            _childR: childR,
            _phase: phase,
          }) - 1;

          links.push({ id: `link:${d.name}:${rd.roundNum}`, fromIdx: devIdx, toIdx: childIdx, status: rd.status });
        }
      }
    });

    const neighbors = new Map();
    const addN = (a, b) => { if (!neighbors.has(a)) neighbors.set(a, new Set()); neighbors.get(a).add(b); };
    links.forEach((l) => { addN(l.fromIdx, l.toIdx); addN(l.toIdx, l.fromIdx); });

    const delays = Object.create(null);
    nodes.forEach(n => { delays[n.id] = n.type === 'server' ? 0 : 0.15 + xfnv1a(n.id)() * 0.5; });
    links.forEach(l => { const key = l.id || `${l.fromIdx}-${l.toIdx}`; delays[key] = 0.25 + xfnv1a(key)() * 0.55; });

    state.nodes = nodes;
    state.links = links;
    state.neighbors = neighbors;
    state.introDelays = delays;
    state.nodeAlpha = new Array(nodes.length).fill(1);
    state.linkAlpha = new Array(links.length).fill(1);
  }

  function getAnimatedXYZ(n, now) {
    const t = (now - state.animStart) / 1000;
    if (n.type === 'server') return { x: 0, y: 0, z: 0 };
    if (n.type === 'device') {
      const angle = (n._basePhase || 0) + state.deviceOrbitSpeed * t;
      const base = mul(n._orbitDir0 || {x:1,y:0,z:0}, n._orbitR || 300);
      return rotateAxis(base, n._orbitAxis || {x:0,y:1,z:0}, angle);
    }
    if (n.type === 'round') {
      const dev = state.nodes[n.parentDevice];
      const devPos = getAnimatedXYZ(dev, now);
      const theta = (n._phase || 0) + state.moonOrbitSpeed * t;
      const offset = add(
        mul(n._u, Math.cos(theta) * (n._childR||80)),
        mul(n._v, Math.sin(theta) * (n._childR||80))
      );
      return add(devPos, offset);
    }
    return { x: n.x||0, y: n.y||0, z: n.z||0 };
  }

  function project(pt, width, height, yaw, pitch, camDist, fovDeg) {
    const cy = Math.cos(yaw), sy = Math.sin(yaw);
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    const x1 = pt.x * cy + pt.z * sy;
    const z1 = -pt.x * sy + pt.z * cy;
    const y2 = pt.y * cp - z1 * sp;
    const z2 = pt.y * sp + z1 * cp;
    const fovRad = (fovDeg * Math.PI) / 180;
    const focal = (height / 2) / Math.tan(fovRad / 2);
    let zCamera = camDist - z2; if (zCamera < 1) zCamera = 1;
    const s = focal / zCamera;
    const cx = width / 2, cyScr = height / 2;
    return { sx: cx + x1 * s, sy: cyScr + y2 * s, scale: s, depth: zCamera };
  }

  function focusOnNode(pos) {
    const targetYaw = Math.atan2(-pos.x, pos.z);
    const cy = Math.cos(targetYaw), sy = Math.sin(targetYaw);
    const z1 = -pos.x * sy + pos.z * cy;
    let targetPitch = Math.abs(z1) > 1e-6 ? Math.atan2(pos.y, z1) : 0;
    targetPitch = Math.max(-Math.PI/2 + 0.01, Math.min(Math.PI/2 - 0.01, targetPitch));
    const startYaw = state.yaw;
    const startPitch = state.pitch;
    const start = performance.now();
    const dur = 650;
    const step = () => {
      const t = Math.min(1, (performance.now() - start) / dur);
      const e = d3.easeCubicInOut(t);
      let dyaw = targetYaw - startYaw;
      while (dyaw > Math.PI) dyaw -= 2*Math.PI;
      while (dyaw < -Math.PI) dyaw += 2*Math.PI;
      state.yaw = startYaw + dyaw * e;
      state.pitch = startPitch + (targetPitch - startPitch) * e;
      requestRender();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function introFrac(now) {
    if (!state.introPlaying) return 1;
    const t = (now - state.introStart) / state.introDur;
    if (t >= 1) { state.introPlaying = false; return 1; }
    return Math.max(0, Math.min(1, t));
  }
  function nodeIntro(id, now) {
    if (id === 'server') return 1;
    const f = introFrac(now);
    const d = state.introDelays[id] ?? 0;
    const p = (f - d) / (1 - d);
    return Math.max(0, Math.min(1, d3.easeCubicOut(Math.max(0, p))));
  }
  function linkIntro(link, now) {
    const f = introFrac(now);
    const key = link.id || `${link.fromIdx}-${link.toIdx}`;
    const d = state.introDelays[key] ?? 0.3;
    const p = (f - d) / (1 - d);
    return Math.max(0, Math.min(1, d3.easeCubicOut(Math.max(0, p))));
  }

  let introZoomStart = 1.5;
  let introZoomEnd = 0.8;
  let introZoomDur = 2000;
  let introZoomStartTime = performance.now();
  function getIntroZoom(now) {
    const t = (now - introZoomStartTime) / introZoomDur;
    const tt = Math.min(Math.max(t, 0), 1);
    const ease = 1 - Math.pow(1 - tt, 3);
    return introZoomStart * (1 - ease) + introZoomEnd * ease;
  }

  function render(ctx, width, heightPx) {
    const now = performance.now();
    const { yaw, pitch, camBaseDist, zoomScale, sceneRadius } = state;
    const zoomAnim = getIntroZoom(now);
    const camDist = Math.max((camBaseDist / Math.max(zoomScale, 0.0001)) * zoomAnim, (sceneRadius || 300) + 80);

    const emphasis = new Set();
    if (state.hoverIndex >= 0) {
      emphasis.add(state.hoverIndex);
      const neigh = state.neighbors.get(state.hoverIndex);
      if (neigh) neigh.forEach((i) => emphasis.add(i));
    }
    const hasHover = emphasis.size > 0;
    const targetNodeAlpha = state.nodes.map((_, i) => (hasHover ? (emphasis.has(i) ? 1 : INACTIVE_NODE_ALPHA) : 1));
    const targetLinkAlpha = state.links.map((l) => (hasHover ? ((emphasis.has(l.fromIdx) && emphasis.has(l.toIdx)) ? 1 : INACTIVE_LINK_ALPHA) : 1));

    const ts = now, dt = Math.min(0.3, (ts - (state.lastTs || ts)) / 1000); state.lastTs = ts;
    const k = 1 - Math.pow(0.001, dt / 0.12);
    if (!state.nodeAlpha.length) state.nodeAlpha = new Array(state.nodes.length).fill(1);
    if (!state.linkAlpha.length) state.linkAlpha = new Array(state.links.length).fill(1);
    for (let i = 0; i < targetNodeAlpha.length; i++) state.nodeAlpha[i] += (targetNodeAlpha[i] - state.nodeAlpha[i]) * k;
    for (let i = 0; i < targetLinkAlpha.length; i++) state.linkAlpha[i] += (targetLinkAlpha[i] - state.linkAlpha[i]) * k;

    ctx.clearRect(0, 0, width, heightPx);
    ctx.fillStyle = '#181818';
    ctx.fillRect(0, 0, width, heightPx);

    if (showRoundOrbits) {
      const ORBIT_SEGMENTS = 64;
      const DASH = [4, 6];
      ctx.save();
      ctx.setLineDash(DASH);
      ctx.lineWidth = 1;
      for (let i = 0; i < state.nodes.length; i++) {
        const n = state.nodes[i];
        if (n.type !== 'round') continue;
        const dev = state.nodes[n.parentDevice];
        const devPos = getAnimatedXYZ(dev, now);
        const childR = n._childR || 80;
        const { _u: u, _v: v } = n;
        const a = (state.linkAlpha[i] ?? 1) * (state.nodeAlpha[n.parentDevice] ?? 1) * nodeIntro(n.id, now);
        if (a <= 0.02) continue;
        let prev = null;
        for (let s = 0; s <= ORBIT_SEGMENTS; s++) {
          const ang = (s / ORBIT_SEGMENTS) * Math.PI * 2;
          const wpos = add(devPos, add(mul(u, Math.cos(ang) * childR), mul(v, Math.sin(ang) * childR)));
          const p = project(wpos, width, heightPx, yaw, pitch, camDist, fov);
          if (prev) {
            ctx.beginPath();
            ctx.moveTo(prev.sx, prev.sy);
            ctx.lineTo(p.sx, p.sy);
            ctx.strokeStyle = (n.color || '#9E9E9E');
            ctx.globalAlpha = 0.25 * a;
            ctx.stroke();
          }
          prev = p;
        }
      }
      ctx.setLineDash([]);
      ctx.restore();
    }

    if (showDeviceOrbits) {
      const ORBIT_SEGMENTS = 96;
      const DASH = [6, 4];
      ctx.save();
      ctx.setLineDash(DASH);
      ctx.lineWidth = 1;
      for (let i = 0; i < state.nodes.length; i++) {
        const n = state.nodes[i];
        if (n.type !== 'device') continue;
        const a = (state.nodeAlpha[i] ?? 1) * nodeIntro(n.id, now);
        if (a <= 0.02) continue;
        const base = mul(n._orbitDir0 || {x:1,y:0,z:0}, n._orbitR || 300);
        const axis = n._orbitAxis || {x:0,y:1,z:0};
        let prev = null;
        for (let s = 0; s <= ORBIT_SEGMENTS; s++) {
          const phi = (s / ORBIT_SEGMENTS) * Math.PI * 2;
          const world = rotateAxis(base, axis, phi);
          const p = project(world, width, heightPx, yaw, pitch, camDist, fov);
          if (prev) {
            ctx.beginPath();
            ctx.moveTo(prev.sx, prev.sy);
            ctx.lineTo(p.sx, p.sy);
            ctx.strokeStyle = n.color || '#9E9E9E';
            ctx.globalAlpha = 0.30 * a;
            ctx.stroke();
          }
          prev = p;
        }
      }
      ctx.setLineDash([]);
      ctx.restore();
    }

    for (let li = 0; li < state.links.length; li++) {
      const link = state.links[li];
      const a = state.nodes[link.fromIdx];
      const b = state.nodes[link.toIdx];
      const aPos = getAnimatedXYZ(a, now);
      const bPos = getAnimatedXYZ(b, now);
      const pa = project(aPos, width, heightPx, yaw, pitch, camDist, fov);
      const pb = project(bPos, width, heightPx, yaw, pitch, camDist, fov);
      const lp = linkIntro(link, now);
      if (lp <= 0) continue;
      const ex = pa.sx + (pb.sx - pa.sx) * lp;
      const ey = pa.sy + (pb.sy - pa.sy) * lp;
      ctx.save();
      ctx.beginPath(); ctx.moveTo(pa.sx, pa.sy); ctx.lineTo(ex, ey);
      const st = link.status || b.status;
      ctx.strokeStyle = STATUS_COLOR[st] || '#999';
      ctx.globalAlpha = (state.linkAlpha[li] ?? 1);
      ctx.lineWidth = 1.5; ctx.stroke(); ctx.restore();
    }

    const ordered = state.nodes.map((n, idx) => {
      const pos = getAnimatedXYZ(n, now);
      return { ...n, idx, _pos: pos };
    }).map(n => ({ ...n, proj: project(n._pos, width, heightPx, yaw, pitch, camDist, fov) }))
      .sort((a, b) => b.proj.depth - a.proj.depth);

    for (const n of ordered) {
      const appear = nodeIntro(n.id, now);
      const alpha = (state.nodeAlpha[n.idx] ?? 1) * appear;
      if (alpha <= 0.001) continue;

      if (n.type === 'round') {
        const r = 10 * (0.6 + 0.4 * appear);
        ctx.save(); ctx.globalCompositeOperation = 'lighter'; ctx.shadowBlur = 18 * appear; ctx.shadowColor = n.color;
        ctx.beginPath(); ctx.arc(n.proj.sx, n.proj.sy, r * 0.95, 0, Math.PI * 2);
        ctx.fillStyle = n.color; ctx.globalAlpha = alpha * 0.85; ctx.fill(); ctx.restore();
        ctx.save(); ctx.beginPath(); ctx.arc(n.proj.sx, n.proj.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = n.color; ctx.globalAlpha = alpha; ctx.fill(); ctx.restore();
        ctx.save(); ctx.fillStyle = '#fff'; ctx.font = '12px ui-monospace, monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.globalAlpha = alpha;
        ctx.fillText(n.label, n.proj.sx, n.proj.sy); ctx.restore();
      } else {
        const font = 'bold 13px ui-monospace, monospace';
        ctx.save(); ctx.font = font;
        const raw = n.label;
        const text = ellipsizeText(ctx, raw, MAX_DEVICE_LABEL_WIDTH);
        const textW = ctx.measureText(text).width;
        const w = Math.max(36, (textW + 20) * (0.7 + 0.3 * appear));
        const h = 26 * (0.7 + 0.3 * appear);
        const x = n.proj.sx - w/2, y = n.proj.sy - h/2;
        ctx.save(); ctx.globalCompositeOperation = 'lighter'; ctx.shadowBlur = 20 * appear; ctx.shadowColor = n.color;
        if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); } else { ctx.beginPath(); ctx.rect(x, y, w, h); }
        ctx.fillStyle = n.color; ctx.globalAlpha = alpha * 0.6; ctx.fill(); ctx.restore();
        ctx.save(); if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); } else { ctx.beginPath(); ctx.rect(x, y, w, h); }
        ctx.fillStyle = n.color; ctx.globalAlpha = alpha; ctx.fill(); ctx.restore();
        ctx.save(); ctx.fillStyle = '#fff'; ctx.font = font; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.globalAlpha = alpha;
        ctx.fillText(text, n.proj.sx, n.proj.sy + 1); ctx.restore();
        ctx.restore();
      }
    }

    requestRender();
  }

  function draw() {
    if (!containerRef.current || !canvasRef.current) return;
    buildGraph();
    const canvas = canvasRef.current;
    const w = containerRef.current.clientWidth || 960;
    canvas.width = w * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    render(ctx, w, height);
  }

  function pickNode(mx, my) {
    const width = containerRef.current?.clientWidth || 960;
    const now = performance.now();
    const zoomAnim = getIntroZoom(now);
    const camDist = Math.max((state.camBaseDist / Math.max(state.zoomScale, 0.0001)) * zoomAnim, (state.sceneRadius || 300) + 80);

    for (let i = state.nodes.length - 1; i >= 0; i--) {
      const n = state.nodes[i];
      const pos = getAnimatedXYZ(n, now);
      const pr = project(pos, width, height, state.yaw, state.pitch, camDist, fov);
      const appear = nodeIntro(n.id, now);

      if (n.type === 'round') {
        const r = 10 * (0.6 + 0.4 * appear);
        const dx = pr.sx - mx, dy = pr.sy - my;
        if (dx * dx + dy * dy <= r * r) return { idx: i, node: n };
      } else {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.font = 'bold 13px ui-monospace, monospace';
        const text = ellipsizeText(ctx, n.label || '', MAX_DEVICE_LABEL_WIDTH);
        const textW = ctx.measureText(text).width;
        const w = Math.max(36, (textW + 20) * (0.7 + 0.3 * appear));
        const h = 26 * (0.7 + 0.3 * appear);
        ctx.restore();
        const x0 = pr.sx - w / 2, y0 = pr.sy - h / 2;
        if (mx >= x0 && mx <= x0 + w && my >= y0 && my <= y0 + h) return { idx: i, node: n };
      }
    }
    return null;
  }

  function showTooltip(hit, mx, my) {
    const el = tooltipRef.current; if (!el) return;
    if (!hit) { el.style.opacity = 0; return; }
    const n = hit.node;
    el.innerHTML = `<div><b>${n.name}</b></div><div>Status: ${n.status}</div>`;
    el.style.opacity = 1;
    el.style.transform = `translate(${mx + 12}px, ${my - 12}px)`;
  }
  function hideTooltip() {
    const el = tooltipRef.current; if (!el) return; el.style.opacity = 0;
  }

  function showInfoPanel(hit) {
    const el = infoRef.current; if (!el) return;
    if (!hit) { el.style.opacity = 0; el.innerHTML = ''; return; }

    let deviceIdx = null;
    let highlightRoundId = null;
    if (hit.node.type === 'device') {
      deviceIdx = hit.idx;
    } else if (hit.node.type === 'round') {
      deviceIdx = hit.node.parentDevice;
      highlightRoundId = hit.node.id;
    }

    if (deviceIdx == null) { el.style.opacity = 0; el.innerHTML = ''; return; }

    const dev = state.nodes[deviceIdx];
    const rounds = state.nodes.filter(n => n.type === 'round' && n.parentDevice === deviceIdx)
      .sort((a,b) => (a.roundNum ?? 0) - (b.roundNum ?? 0));

    const devStatusDot = `<span class="${styles.dot}" style="background:${STATUS_COLOR[dev.status] || '#9E9E9E'}"></span>`;
    const rows = rounds.map(r => {
      const dot = `<span class="${styles.dot}" style="background:${STATUS_COLOR[r.status] || '#9E9E9E'}"></span>`;
      const hi = r.id === highlightRoundId ? styles.roundItemActive : '';
      return `<div class="${styles.roundItem} ${hi}">
        <div class="${styles.left}">${dot}<span class="${styles.roundId}">${r.roundNum}R</span></div>
        <div class="${styles.right}">${r.status}</div>
      </div>`;
    }).join('');

    const latest = dev.latestRound != null ? `${dev.latestRound}R` : '-';
    el.innerHTML = `
      <div class="${styles.infoHeader}">
        <div class="${styles.infoTitle}">${devStatusDot}${dev.name}</div>
        <div class="${styles.infoSub}">Latest: ${latest}</div>
      </div>
      <div class="${styles.infoSection}">
        <div class="${styles.sectionTitle}">Rounds</div>
        <div class="${styles.roundList}">${rows || '<div class="${styles.empty}">No rounds</div>'}</div>
      </div>
    `;
    el.style.opacity = 1;
  }
  function hideInfoPanel() {
    const el = infoRef.current; if (!el) return;
    el.style.opacity = 0;
  }

  let rafId = null;
  function requestRender() { cancelAnimationFrame(rafId); rafId = requestAnimationFrame(() => draw()); }

  return (
    <div ref={containerRef} className={styles.root} style={{ height, ...(style||{}) }}>
      <canvas ref={canvasRef} className={styles.canvas} />
      <div ref={tooltipRef} className={styles.tooltip} />
      <div ref={infoRef} className={styles.infoPanel} />
    </div>
  );
}

function useResizeObserver(ref, onResize) {
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(() => onResize?.());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref, onResize]);
}
